import React from 'react';
import { 
  Clock, TrendingUp, Cloud, Activity, Sparkles, 
  BarChart3, History, Thermometer, MessageSquare
} from "lucide-react";

/**
 * Shimmer loading effect component
 * Use this for panels that are still loading data
 */
export const Shimmer = ({ className = '', width = '100%', height = '20px', borderRadius = '4px' }) => {
  return (
    <div 
      className={`shimmer ${className}`}
      style={{ 
        width, 
        height, 
        borderRadius,
      }}
    />
  );
};

export const ShimmerText = ({ lines = 3, className = '' }) => {
  return (
    <div className={`shimmer-text ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer 
          key={i} 
          width={i === lines - 1 ? '60%' : '100%'} 
          height="12px"
          borderRadius="6px"
        />
      ))}
    </div>
  );
};

export const ShimmerCard = ({ className = '' }) => {
  return (
    <div className={`shimmer-card ${className}`}>
      <Shimmer width="40%" height="14px" borderRadius="7px" />
      <div className="shimmer-card-body">
        <Shimmer width="100%" height="40px" borderRadius="8px" />
      </div>
      <ShimmerText lines={2} />
    </div>
  );
};

/**
 * Panel shimmer with title and loading message
 * Shows: Icon + Title + "Loading..." message + shimmer effect
 */
export const ShimmerPanel = ({ type = 'default', title = '', className = '' }) => {
  // Map panel types to their icons and default titles
  const panelConfig = {
    forecast: { Icon: Clock, defaultTitle: '24H Forecast', message: 'Loading forecast data...' },
    stocks: { Icon: TrendingUp, defaultTitle: 'Mood vs Markets', message: 'Fetching market data...' },
    social: { Icon: MessageSquare, defaultTitle: 'Social Pulse', message: 'Analyzing social trends...' },
    weather: { Icon: Thermometer, defaultTitle: 'Weather Influence', message: 'Loading weather data...' },
    prediction: { Icon: Sparkles, defaultTitle: 'AI Prediction', message: 'Generating prediction...' },
    matrix: { Icon: BarChart3, defaultTitle: 'Mood Matrix', message: 'Calculating mood position...' },
    timetravel: { Icon: History, defaultTitle: 'Time Travel', message: 'Loading historical data...' },
    wordcloud: { Icon: Cloud, defaultTitle: 'Key Topics', message: 'Analyzing keywords...' },
    events: { Icon: Activity, defaultTitle: 'Event Impact', message: 'Processing events...' },
    default: { Icon: Activity, defaultTitle: 'Loading', message: 'Loading data...' }
  };

  const config = panelConfig[type] || panelConfig.default;
  const Icon = config.Icon;
  const displayTitle = title || config.defaultTitle;

  switch (type) {
    case 'forecast':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel shimmer-forecast">
            <div className="shimmer-forecast-items">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="shimmer-forecast-item">
                  <Shimmer width="50px" height="50px" borderRadius="12px" />
                  <Shimmer width="60%" height="10px" borderRadius="5px" />
                  <Shimmer width="40%" height="8px" borderRadius="4px" />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    
    case 'stocks':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel shimmer-stocks">
            <div className="shimmer-stocks-grid">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="shimmer-stock-item">
                  <Shimmer width="40px" height="16px" borderRadius="4px" />
                  <Shimmer width="50px" height="12px" borderRadius="4px" />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    
    case 'social':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel shimmer-social">
            <div className="shimmer-social-content">
              <Shimmer width="80px" height="80px" borderRadius="50%" />
              <ShimmerText lines={3} />
            </div>
          </div>
        </div>
      );
    
    case 'weather':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel shimmer-weather">
            <div className="shimmer-weather-content">
              <Shimmer width="60px" height="60px" borderRadius="12px" />
              <div className="shimmer-weather-info">
                <Shimmer width="80%" height="14px" borderRadius="7px" />
                <Shimmer width="60%" height="10px" borderRadius="5px" />
              </div>
            </div>
            <Shimmer width="100%" height="8px" borderRadius="4px" />
            <Shimmer width="100%" height="8px" borderRadius="4px" />
          </div>
        </div>
      );
    
    case 'prediction':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel shimmer-prediction">
            <Shimmer width="60%" height="32px" borderRadius="8px" className="mx-auto" />
            <Shimmer width="40%" height="10px" borderRadius="5px" className="mx-auto" />
            <ShimmerText lines={2} />
          </div>
        </div>
      );

    case 'matrix':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel">
            <Shimmer width="100%" height="150px" borderRadius="12px" />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Shimmer width="50%" height="20px" borderRadius="4px" />
              <Shimmer width="50%" height="20px" borderRadius="4px" />
            </div>
          </div>
        </div>
      );

    case 'timetravel':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel">
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Shimmer key={i} width="40px" height="40px" borderRadius="8px" />
              ))}
            </div>
            <Shimmer width="100%" height="60px" borderRadius="8px" />
          </div>
        </div>
      );

    case 'wordcloud':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[80, 60, 90, 50, 70, 40, 85, 55].map((w, i) => (
                <Shimmer key={i} width={`${w}px`} height="24px" borderRadius="12px" />
              ))}
            </div>
          </div>
        </div>
      );

    case 'events':
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel">
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <Shimmer width="8px" height="8px" borderRadius="50%" />
                <Shimmer width="85%" height="16px" borderRadius="4px" />
              </div>
            ))}
          </div>
        </div>
      );
    
    default:
      return (
        <div className={`shimmer-panel-wrapper ${className}`}>
          <div className="shimmer-panel-header">
            <Icon size={14} className="shimmer-panel-icon" />
            <span className="shimmer-panel-title">{displayTitle}</span>
          </div>
          <div className="shimmer-panel-message">{config.message}</div>
          <div className="shimmer-panel">
            <Shimmer width="100%" height="60px" borderRadius="8px" />
            <ShimmerText lines={2} />
          </div>
        </div>
      );
  }
};

export default Shimmer;
